import React, { useEffect, useState } from "react";
import { getTalentEmailReport } from "../../services/adminTalentEmails";
import type { TalentEmailConversions, TalentEmailDays, TalentEmailReport, TalentEmailRow, TalentEmailWindowDays } from "../../types/talentEmails";
import { fmtN, pctOf } from "./format";
import { Cell, Fx, Row, Sheet } from "./Sheet";
import "./TalentEmailsSheet.css";

// The "Talent emails" sheet: one row per talent lifecycle email. How many went
// out, delivery (once the Resend webhook is connected), who tapped a button in
// it (lib/emailClick logs the taps), who unsubscribed after it, and how many of
// the talent who got it took each next step within 7 or 14 days — of them, how
// many had tapped the email first. Counts PEOPLE, like the rest of the tab.
// Tapped and Unsubscribed are a % of emails sent; a next step is a % of the
// emails old enough to count (sent at least 7 or 14 days ago, so every talent
// had the full time).
//
// It owns its data and its windows: the server answers 7, 30 or 90 days of
// emails (GET /admin/talent-emails, services/adminTalentEmails) with both
// next-step windows in one answer, so both switches sit on the sheet, apart
// from the tab's range pills, and the 7 / 14 days switch needs no new load.

const RANGES: { days: TalentEmailDays; label: string }[] = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

const WINDOWS: { days: TalentEmailWindowDays; label: string }[] = [
  { days: 7, label: "7 days" },
  { days: 14, label: "14 days" },
];

const NEXT_STEPS: { key: keyof TalentEmailConversions; label: string }[] = [
  { key: "payoutStarted", label: "Started payouts" },
  { key: "payoutReady", label: "Finished payouts" },
  { key: "approved", label: "Approved" },
  { key: "firstRequest", label: "First request" },
  { key: "firstEarning", label: "First earning" },
];

const COULD_NOT_LOAD = "Couldn't load the talent emails. Retry in a moment.";
const NOT_ON_SERVER = "The talent email report isn't on the server yet. It appears once the server update is live.";

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const dayCount = (n: number) => `${n} day${n === 1 ? "" : "s"}`;

const plain = (n: number | null): Cell => ({ v: fmtN(n), cls: `gr-sh-num${n == null ? " gr-sh-none" : ""}` });

// A count with its share of the emails sent underneath.
const ofSent = (n: number | null, sent: number | null): Cell =>
  n == null
    ? plain(n)
    : {
        v: (
          <>
            {fmtN(n)}
            <span className="adm-te-pct">{pctOf(n, sent)}</span>
          </>
        ),
        cls: "gr-sh-num",
      };

// A next step: the count, its share of the emails old enough to count, and how
// many of those talent had tapped the email first. "—" until an email is old enough.
const stepCell = (n: number | null, counted: number | null, tapped: number | null): Cell =>
  n == null || !counted
    ? plain(null)
    : {
        v: (
          <>
            {fmtN(n)}
            <span className="adm-te-pct">{pctOf(n, counted)}</span>
            <span className="adm-te-tap">{fmtN(tapped ?? 0)} tapped</span>
          </>
        ),
        cls: "gr-sh-num",
      };

const rowFor = (r: TalentEmailRow, win: TalentEmailWindowDays): Row => {
  const sent = num(r.sent);
  const w = r.windows?.[win];
  const counted = num(w?.counted);
  return {
    cells: [
      { v: <span className="adm-te-name">{r.label || r.key}</span>, cls: "gr-sh-step", title: r.key },
      plain(sent),
      plain(num(r.delivered)),
      plain(num(r.bounced)),
      plain(num(r.complained)),
      ofSent(num(r.clicked), sent),
      ofSent(num(r.unsubscribed), sent),
      ...NEXT_STEPS.map((s) => stepCell(num(w?.converted?.[s.key]), counted, num(w?.afterTap?.[s.key]))),
    ],
  };
};

const headFor = (win: TalentEmailWindowDays): Row[] => [
  {
    cls: "gr-sh-grp",
    cells: [{ v: "" }, { v: "Delivery", span: 4 }, { v: "", span: 2 }, { v: `Next step within ${dayCount(win)}`, span: 5 }],
  },
  {
    cls: "gr-sh-head",
    cells: [
      { v: "Email", cls: "gr-sh-step" },
      { v: "Sent" },
      { v: "Delivered" },
      { v: "Bounced" },
      { v: "Spam" },
      { v: "Tapped" },
      { v: "Unsubscribed" },
      ...NEXT_STEPS.map((s) => ({ v: s.label })),
    ],
  },
];

// The sheet's one finding, in plain words (the workbook's fx line).
function finding(report: TalentEmailReport | null, days: number): React.ReactNode {
  if (!report) return "Every talent email: how many went out, who tapped it, and what talent did next.";
  const period = dayCount(num(report.days) ?? days);
  const rows = report.emails;
  if (rows.length === 0) return `No talent emails went out in the last ${period}.`;
  const top = rows.reduce((best, r) => ((num(r.clicked) ?? 0) > (num(best.clicked) ?? 0) ? r : best), rows[0]);
  if (!((num(top.clicked) ?? 0) > 0)) {
    const sent = rows.reduce((sum, r) => sum + (num(r.sent) ?? 0), 0);
    return `${fmtN(sent)} talent emails went out in the last ${period}. No taps yet.`;
  }
  return (
    <>
      Most tapped: <b>{top.label || top.key}</b>, {fmtN(top.clicked)} of {fmtN(num(top.sent))} sent (
      {pctOf(top.clicked, num(top.sent))}).
    </>
  );
}

export function TalentEmailsSheet() {
  const [days, setDays] = useState<TalentEmailDays>(30);
  const [win, setWin] = useState<TalentEmailWindowDays>(7);
  const [report, setReport] = useState<TalentEmailReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0); // Retry re-runs the load

  useEffect(() => {
    let live = true; // a slower answer for a window no longer picked is dropped
    setLoading(true);
    setError(null);
    getTalentEmailReport(days)
      .then((r) => {
        if (!r || !Array.isArray(r.emails)) throw new Error("unreadable report");
        if (live) setReport(r);
      })
      .catch((e: { response?: { status?: number } }) => {
        if (!live) return;
        // Never the last window's rows under the new window's name.
        setReport(null);
        setError(e?.response?.status === 404 ? NOT_ON_SERVER : COULD_NOT_LOAD);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [days, attempt]);

  const rows = report?.emails ?? [];
  // No email old enough for this window yet (the first days after the switch-on).
  const noneOldEnough = rows.every((r) => !num(r.windows?.[win]?.counted));
  const deliveryMissing =
    !!report && (!report.deliveryTracking || rows.some((r) => num(r.delivered) == null || num(r.bounced) == null || num(r.complained) == null));

  return (
    <>
      <Fx>{finding(report, days)}</Fx>
      <div className="adm-te-bar">
        <span className="gr-muted">Emails sent in the last</span>
        <span className="gr-seg" role="group" aria-label="Report window">
          {RANGES.map((r) => (
            <button key={r.days} type="button" className={days === r.days ? "on" : ""} aria-pressed={days === r.days} onClick={() => setDays(r.days)}>
              {r.label}
            </button>
          ))}
        </span>
        <span className="gr-muted">Next steps within</span>
        <span className="gr-seg" role="group" aria-label="Next-step window">
          {WINDOWS.map((w) => (
            <button key={w.days} type="button" className={win === w.days ? "on" : ""} aria-pressed={win === w.days} onClick={() => setWin(w.days)}>
              {w.label}
            </button>
          ))}
        </span>
        {loading && report ? <span className="gr-muted">refreshing…</span> : null}
      </div>
      {!report ? (
        loading ? (
          <div className="adm-card gr-muted">Loading the talent emails…</div>
        ) : (
          <div className="adm-card">
            <span className="gr-muted">{error || COULD_NOT_LOAD}</span>{" "}
            <button type="button" className="gr-retry" onClick={() => setAttempt((n) => n + 1)}>
              Retry
            </button>
          </div>
        )
      ) : rows.length === 0 ? (
        <div className="gr-muted gr-pad-top">No talent emails sent in this period.</div>
      ) : (
        <>
          <div className="adm-te-sheet">
            <Sheet cols={12} head={headFor(win)} rows={rows.map((r) => rowFor(r, win))} label="talent emails" />
          </div>
          <div className="gr-note">
            Next steps count the emails sent at least {dayCount(win)} ago, so every talent had the full {dayCount(win)}. Their % is of
            those emails; “tapped” = how many had tapped the email first.
          </div>
          <div className="gr-note">
            Tapped = talent who tapped a button in it (a device, when signed out). Unsubscribed = talent who unsubscribed with it as the
            last tip they got. Both % are of emails sent.
          </div>
          {noneOldEnough && <div className="gr-note">No email is {dayCount(win)} old yet. Next steps fill in from then.</div>}
          {deliveryMissing && <div className="gr-note">Delivered / bounced / spam appear once the Resend webhook is connected.</div>}
        </>
      )}
    </>
  );
}
